import { useHistory } from 'react-router-dom';
import { CommerceVariant, ProductOnUser } from 'common-ts/dist/models/Product';
import { RegularSizeValue, SizeType } from 'common-ts/dist/models/Cart';
import {
  addItemCount,
  postCanAddItemCount,
  resetCartAddressAndPaymentState,
  updateAddressInOrder,
  updateUserTrigger,
} from '../redux/CartRedux';
import { resetAddressDraft, inputAddressForm } from '../redux/AddressRedux';
import { useAppDispatch } from '../reduxhooks';
import { Address } from 'common-ts/dist/models/Address';

// Shared helpers for the /link (link-in-bio) page components.

export function formatRupiah(value: number): string {
  return 'Rp ' + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function variantIsPurchasable(v: CommerceVariant): boolean {
  if (!v.inventoryTracked) return true;
  if (v.inventoryPolicy === 'CONTINUE') return true;
  return v.inventoryQuantity > 0;
}

// The price a buyer actually pays on the website (mapper convention:
// discountedPrice === -1 means no discount).
export function hargaLangsung(product: ProductOnUser): number {
  return product.discountedPrice !== -1 ? product.discountedPrice : product.originalPrice;
}

// Comparison honesty: only advertise a marketplace price when it is really
// higher than the direct price — never render a worse-looking deal.
export function marketplaceComparison(product: ProductOnUser): {
  hargaLangsung: number;
  hargaMarketplace: number;
  hemat: number;
  hematPercent: number;
} | null {
  const direct = hargaLangsung(product);
  const marketplace = product.marketplacePrice;
  if (!marketplace || marketplace <= direct) return null;
  const hemat = marketplace - direct;
  return {
    hargaLangsung: direct,
    hargaMarketplace: marketplace,
    hemat,
    hematPercent: Math.round((hemat / marketplace) * 100),
  };
}

// Optional checkout-address region prefill from the ongkir estimate dialog.
export interface RegionPrefill {
  provinsi: string;
  kabupaten: string;
  kecamatan: string;
  kodePos?: number;
  destinationKiriminAjaObject: any;
}

// Buy-now: stock-guarded add-to-cart then straight to /checkout, skipping
// the cart window. Same cartFields contract as the product-page buy box
// (PageProductDetailCommerceOptions.handleAddToCart).
export function useLinkPageBuyNow() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  return async (
    product: ProductOnUser,
    variant: CommerceVariant,
    regionPrefill?: RegionPrefill,
  ): Promise<boolean> => {
    const cartFields = {
      productId: product.productId,
      fitType: product.defaultFitType,
      sizeType: SizeType.regular,
      measurementCreationDateTime: 'NOTSELECTED',
      measurementUpdateDateTime: 'NOTSELECTED',
      measurementIsBodyMeasurement: false,
      regularSize: RegularSizeValue.NOTSELECTED,
      variantId: variant.id,
      variantTitle: variant.title,
      unitPrice: variant.price,
      compareAtPrice: variant.compareAtPrice,
    };

    // canAddItemDict must know this item before the addItemCount reducer
    // accepts it; the thunk also answers the stock question locally.
    const result = await dispatch(postCanAddItemCount({
      ...cartFields,
      amount: 1,
      clothingType: product.clothingType,
    }));
    const canAdd = (result.payload as { canBeProvisioned?: boolean } | undefined)?.canBeProvisioned;
    if (!canAdd) return false;

    dispatch(addItemCount({ ...cartFields, productDetail: product }));

    if (regionPrefill) {
      // PageCheckout wipes address state when userTriggerCheckout is set, so
      // for the prefilled path we do that reset ourselves, then seed both the
      // cart's shipping fields and the address form draft with the region the
      // shopper already picked in the ongkir dialog.
      dispatch(resetCartAddressAndPaymentState());
      dispatch(resetAddressDraft());
      const prefill: Address = {
        provinsi: regionPrefill.provinsi,
        kabupaten: regionPrefill.kabupaten,
        kecamatan: regionPrefill.kecamatan,
        kodePos: regionPrefill.kodePos,
        kotaKecamatanText: `${regionPrefill.kabupaten}, ${regionPrefill.kecamatan}`,
        destinationKiriminAjaObject: regionPrefill.destinationKiriminAjaObject,
      };
      dispatch(inputAddressForm(prefill));
      dispatch(updateAddressInOrder(prefill));
    } else {
      dispatch(updateUserTrigger(true));
    }
    history.push('/checkout');
    return true;
  };
}
